import React from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import { inject, observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import CommonButton from "@/views/CommonComponent/CommonButton";
import CommonSwitch from "@/views/CommonComponent/Styled/CommonSwitch";
import TitleBox from "../CommonComponent/TitleBox";
import {
  MemoryConsentRoot,
  MemoryConsentContents,
  MemoryConsentBody,
  MemoryCard,
  MemoryCardHeader,
  MemoryCardTitle,
  MemoryCardDescription,
  GuideSection,
  GuideTitle,
  GuideList,
  GuideText,
} from "./Styled/MemoryConsentStyled";
import { InfoBox, SignInContentsBox, SignInProgressBarContainer, SignInProgressTrack, SignInProgressFill } from "./Styled/SignInStyled";
import { ButtonContainer } from "./Styled/SignInStyled";
import { Icon } from '@iconify/react';
import * as store from "../../stores/AuthStore";
import { useToast } from "../../components/useToast";
import { queuePokoroProfileCompleteReturnToSetting } from "../../common/pokoroProfileReturnFlow";

function MemoryConsent(props) {
  const { intl, authStore, userStore } = props;
  const history = useHistory();
  const location = history.location;
  const { showErrorToast } = useToast();

  const handleToggleMemory = (event) => {
    authStore.setIsMemoryEnabled(event.target.checked);
  };

  const handleToggleMemoryByCard = () => {
    authStore.setIsMemoryEnabled(!authStore.isMemoryEnabled);
  };

  const handleMemorySwitchClick = (event) => {
    event.stopPropagation();
  };

  const handleMemoryCardKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleToggleMemoryByCard();
    }
  };

  const postProfileDestination = () => {
    return location.state?.redirectUrl ? location.state.redirectUrl : localStorage.getItem(store.LocalStorageConnectSkip) ? "/home" : "/pokoroConnect";
  }


  const handleClickComplete = () => {
    const notNewUser = !!location.state?.notNewUser;
    const routedDeviceId = location.state?.deviceId || null;
    const returnToPokoroSettingAfterProfile =
      !!location.state?.returnToPokoroSettingAfterProfile;

    const goNext = () => {
      history.push(postProfileDestination());
    };

    const finishPokoroSettingProfileFlow = () => {
      queuePokoroProfileCompleteReturnToSetting(
        intl.formatMessage({ id: "profile_registration_complete_toast" })
      );
      history.push("/pokoroSetting");
    };

    (async () => {
      try {
        // [신규/기존 공통] 기기 수에 따라 deviceId 포함 여부/선택 화면 분기
        const userId = authStore.loginUser.id;
        const response = await userStore.userRepository.getUserDeviceList(userId);
        const devices = Array.isArray(response.data) ? response.data : [];
        const nullProfileDevices = devices.filter(device => device.profile === null);

        if (nullProfileDevices.length >= 2) {
          history.push("/signin/select-pokoro-device", {
            deviceList: nullProfileDevices,
            notNewUser: location.state?.notNewUser,
            redirectUrl: location.state?.redirectUrl,
            returnToPokoroSettingAfterProfile:
              location.state?.returnToPokoroSettingAfterProfile,
          });
          return;
        }

        // 이전 단계(onSetDeviceId 등)에서 이미 받은 deviceId가 있으면 우선 사용한다.
        // 프로필이 없는 기기가 정확히 1대면(계정 전체 기기 수와 무관) 그 기기에 연결한다.
        const deviceId =
          routedDeviceId ??
          (nullProfileDevices.length === 1
            ? nullProfileDevices[0].deviceId
            : null);
        if (!notNewUser) {
          // [신규 회원] 약관동의 + user_device_profiles 단일 API
          authStore.completeSignupWithUserDeviceProfile(deviceId, () => {
            // 신규 회원 완료 시에는 무조건 /pokoroConnect로 가도록 skip 플래그를 제거한다.
            localStorage.removeItem(store.LocalStorageConnectSkip);
            history.push("/pokoroConnect");
          });
          return;
        }

        authStore.saveUserDeviceProfile(deviceId, async (saveResponse) => {
          if (!saveResponse) {
            if (returnToPokoroSettingAfterProfile) {
              showErrorToast(
                intl.formatMessage({ id: "profile_registration_error_toast" })
              );
            }
            return;
          }
          if (returnToPokoroSettingAfterProfile) {
            try {
              if (deviceId) {
                await userStore.userRepository.updateConnectedDevice(
                  userId,
                  deviceId
                );
                userStore.getUserDeviceList(userId, true);
              }
            } catch (e) {
              console.error(e);
              showErrorToast(
                intl.formatMessage({ id: "profile_registration_error_toast" })
              );
              return;
            }
            finishPokoroSettingProfileFlow();
            return;
          }
          if (deviceId && nullProfileDevices.length === 0) {
            userStore.updateConnectedDevice(userId, deviceId, () => {
              goNext();
            });
            return;
          }
          userStore.setSelectedDevice(userStore.userDeviceList.find(item => item.deviceId === deviceId));
          goNext();
        });
      } catch (e) {
        console.error(e);
        if (returnToPokoroSettingAfterProfile) {
          showErrorToast(
            intl.formatMessage({ id: "profile_registration_error_toast" })
          );
          return;
        }
        goNext();
      }
    })();
  };

  const InfoProgressBar = React.memo(function InfoProgressBarComponent(props) {
    const { percent } = props;

    return (
      <SignInProgressBarContainer aria-label="회원가입 진행률">
        <SignInProgressTrack role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
          <SignInProgressFill percent={percent} />
        </SignInProgressTrack>
      </SignInProgressBarContainer>
    );
  });

  return (
    <MemoryConsentRoot>
      <TitleBox
        title={intl.formatMessage({ id: "memory_consent_title" })} /* 포코로와 했던 대화를 기억하도록 설정할 수 있어요. */
        subtitle={intl.formatMessage({ id: "memory_consent_subtitle" })} /* 최근 대화를 기억해 다음 대화에 자연스럽게 반영해요. */
        showBackButton={false}
      />
      {!location.state?.notNewUser && <InfoProgressBar percent={100} />}
      <SignInContentsBox>
        <InfoBox mt={1}>
          <MemoryCard
            role="button"
            tabIndex={0}
            onClick={handleToggleMemoryByCard}
            onKeyDown={handleMemoryCardKeyDown}
          >
            <MemoryCardHeader>
              <MemoryCardTitle>
                {intl.formatMessage({ id: "memory_consent_card_title" })} {/* 기억하기 기능 */}
              </MemoryCardTitle>
              <CommonSwitch
                checked={authStore.isMemoryEnabled}
                onChange={handleToggleMemory}
                onClick={handleMemorySwitchClick}
                inputProps={{
                  "aria-label": intl.formatMessage({
                    id: "memory_consent_card_toggle_aria",
                  }),
                }}
              />
            </MemoryCardHeader>
            <MemoryCardDescription>
              {intl.formatMessage({ id: "memory_consent_card_description" })} {/* 이용자와 나눈 최근 대화를 기억해요.\n좋아하는 것과 관심사 경헙, 가족 이야기, 대화 스타일 등을 기억해 다음 대화에 반영해요. */}
            </MemoryCardDescription>
          </MemoryCard>

          <GuideSection>
            <GuideTitle>
              <Icon icon="glyphs-poly:lock" fontSize={20} />
              {intl.formatMessage({ id: "memory_consent_guide_title" })} {/* 데이터 보호 및 관리 안내 */}
            </GuideTitle>
            <GuideList>
              <GuideText>
                {intl.formatMessage({ id: "memory_consent_guide_body" })} {/* • 이용자의 관심사, 경험, 선호하는 설명 방식 등 맞춤 대화에 필요한 정보만 기억합니다. */}
              </GuideText>
              <GuideText>
                {intl.formatMessage({ id: "memory_consent_guide_body2" })} {/* • 연락처, 주소 등 민감한 정보는 자동으로 차단되어 저장되지 않습니다. */}
              </GuideText>
              <GuideText>
                {intl.formatMessage({ id: "memory_consent_guide_body3" })} {/* • 저장 정보는 최대 50개까지만 유지되며, 오래된 기록은 자동으로 삭제됩니다. */}
              </GuideText>
            </GuideList>
          </GuideSection>
        </InfoBox>

        <ButtonContainer>
          <CommonButton
            text={intl.formatMessage({ id: "memory_consent_complete_button" })}
            background="#FDD751"
            color="#333"
            onClick={handleClickComplete}
          />
        </ButtonContainer>
      </SignInContentsBox>
    </MemoryConsentRoot>
  );
}

export default injectIntl(inject("authStore", "userStore")(observer(MemoryConsent)));

